import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { maybe } from "@saleor/misc";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";
import moment from "moment";

import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  //   dedupeFilter,
  getMultipleValueQueryParam
} from "../../../../../src/utils/filters";
import { IFilterElement } from "../../../../components/Filter";
import { removeBase64Name } from "../../../utils/misc";
import { GrabExpressFilterKeys } from "../../components/GrabExpressListPage/filters";
import { GrabExpressListFilterOpts } from "../../components/GrabExpressListPage/filters";
import { InitialGrabExpressFilterData_malls_edges_node } from "../../types/InitialGrabExpressFilterData";
import {
  GrabExpressListUrlFilters,
  GrabExpressListUrlFiltersEnum,
  GrabExpresssListUrlFiltersWithMultipleValues,
  GrabExpressUrlQueryParams
} from "../../url";

export const GRAB_EXPRESS_FILTERS_KEY = "GrabExpressFilters";

export function getFilterOpts(
  params: GrabExpressListUrlFilters,
  malls: {
    initial: InitialGrabExpressFilterData_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): GrabExpressListFilterOpts {
  return {
    completedAt: {
      active: maybe(
        () =>
          [params.orderCompleteDateFrom, params.orderCompleteDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderCompleteDateTo, ""),
        min: maybe(() => params.orderCompleteDateFrom, "")
      }
    },
    createdDate: {
      active: maybe(
        () =>
          [params.createdFrom, params.createdTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.createdTo, ""),
        min: maybe(() => params.createdFrom, "")
      }
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    orderStatus: {
      active: !!params.orderStatus,
      value: maybe(() => dedupeFilter(params.orderStatus), null)
    },
    shippingMethodName: {
      active: !!params.deliveryType,
      value: maybe(() => dedupeFilter(params.deliveryType), null)
    }
  };
}

export function getFilterVariables(params: GrabExpressListUrlFilters) {
  const newDate = new Date();
  const endDay = newDate.getDate();
  const endMonth = newDate.getMonth() + 1;
  const endYear = newDate.getFullYear();
  const startDate = moment(newDate.setMonth(newDate.getMonth() - 3)).format(
    "YYYY-MM-DD"
  );
  const endDate = moment(`${endYear}-${endMonth}-${endDay}`)
    .add(1, "days")
    .format("YYYY-MM-DD");
  return {
    completedAt: getGteLteVariables({
      gte: params.orderCompleteDateFrom
        ? moment(`${params.orderCompleteDateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : null,
      lte: params.orderCompleteDateTo
        ? moment(`${params.orderCompleteDateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : null
    }),
    mallId: removeBase64Name(
      "Mall:",
      params.mall !== undefined ? dedupeFilter(params.mall) : []
    ),
    orderDate: getGteLteVariables({
      gte: params.createdFrom
        ? moment(`${params.createdFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : !params.createdFrom && params.orderCompleteDateFrom
        ? null
        : moment(`${startDate} 00:00:00`).format("YYYY-MM-DD HH:mm:ssZZ"),
      lte: params.createdTo
        ? moment(`${params.createdTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : !params.createdTo && params.orderCompleteDateTo
        ? null
        : endDate
    }),
    orderStatus: maybe(() => params.orderStatus, null),
    search: params.query,
    shippingMethodName: maybe(() => params.deliveryType, null)
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<GrabExpressFilterKeys>,
  params: GrabExpressListUrlFilters
): GrabExpressListUrlFilters {
  const { active, group, name, value } = filter;

  if (!!group) {
    const rest = params && params[group] ? params[group] : undefined;

    return {
      [group]: active
        ? {
            ...(rest === undefined ? {} : rest),
            [name]: value
          }
        : rest
    };
  }

  switch (name) {
    case GrabExpressFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        GrabExpresssListUrlFiltersWithMultipleValues.mall
      );
    case GrabExpressFilterKeys.createdDate:
      return getMinMaxQueryParam(
        filter,
        GrabExpressListUrlFiltersEnum.createdFrom,
        GrabExpressListUrlFiltersEnum.createdTo
      );
    case GrabExpressFilterKeys.orderStatus:
      return getMultipleValueQueryParam(
        filter,
        GrabExpresssListUrlFiltersWithMultipleValues.orderStatus
      );
    case GrabExpressFilterKeys.shippingMethodName:
      return getMultipleValueQueryParam(
        filter,
        GrabExpresssListUrlFiltersWithMultipleValues.deliveryType
      );
    case GrabExpressFilterKeys.completedAt:
      return getMinMaxQueryParam(
        filter,
        GrabExpressListUrlFiltersEnum.orderCompleteDateFrom,
        GrabExpressListUrlFiltersEnum.orderCompleteDateTo
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<GrabExpressListUrlFilters>(GRAB_EXPRESS_FILTERS_KEY);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  GrabExpressUrlQueryParams,
  GrabExpressListUrlFilters
>({
  ...GrabExpressListUrlFiltersEnum,
  ...GrabExpresssListUrlFiltersWithMultipleValues
});
